<template>
  <DetailCard outlined :fillHeight="false">
    <v-progress-linear v-if="showLoading" indeterminate color="primary"/>
    <v-data-table
    v-else-if="!loading"
    dense
    hide-default-footer
    item-key="key"
    class="small-headers"
    :headers="headers"
    :items="rows"
    :items-per-page="-1"
    :item-class="rowClass">
      <template #item.comparison="{ item }">
        <v-icon small :color="comparisonColor(item.comparison)" class="mr-1">{{ comparisonIcon(item.comparison) }}</v-icon>
        <span :class="{ 'font-weight-medium': item.comparison !== LineComparison.Match }">{{ $t(comparisonLabelKey(item.comparison)) }}</span>
      </template>
      <template #item.speciesGroup="{ item }">{{ item.line.speciesGroup?.label ?? $t('all') }}</template>
      <template #item.lengthGroup="{ item }">{{ item.line.lengthGroup?.label ?? $t('all') }}</template>
      <template #item.diameterGroup="{ item }">{{ item.line.diameterGroup?.label ?? $t('all') }}</template>
      <template #item.grade="{ item }">{{ item.line.grade?.name ?? $t('all') }}</template>
      <template #item.endUseSort="{ item }">{{ item.line.endUseSort?.name ?? $t('all') }}</template>
      <template #item.definitionRate="{ item }">{{ rateString(item.definitionRate) }}</template>
      <template #item.instanceRate="{ item }">
        <span :class="{ 'font-weight-bold': item.comparison !== LineComparison.Match && item.instanceRate !== null }">{{ rateString(item.instanceRate) }}</span>
      </template>
      <template #item.difference="{ item }">{{ rateString(item.difference) }}</template>
    </v-data-table>
  </DetailCard>
</template>

<script>
import { formatMoney } from '@/utils/NumericMutations'
import { compareLines, LineComparison } from '@/components/contract/rate-sheet-divergence/RateSheetDivergence'

const LOADING_INDICATOR_DELAY_MS = 300

export default {
  name: 'RateSheetDivergenceLines',

  components: {
    DetailCard: () => import('@/components/core/DetailCard.vue')
  },

  props: {
    definition: {
      type: Object,
      required: true
    },
    instanceLines: {
      type: Array,
      default: undefined
    }
  },

  data: () => ({
    LineComparison,
    showLoading: false,
    loadingTimer: undefined
  }),

  watch: {
    loading: {
      immediate: true,
      handler (loading) {
        clearTimeout(this.loadingTimer)
        this.showLoading = false
        if (loading) this.loadingTimer = setTimeout(() => { this.showLoading = true }, LOADING_INDICATOR_DELAY_MS)
      }
    }
  },

  beforeDestroy () {
    clearTimeout(this.loadingTimer)
  },

  computed: {
    loading () {
      return !Array.isArray(this.instanceLines)
    },

    rows () {
      return this.loading ? [] : compareLines(this.definition.latestVersion.rateSheetDefinitionLines, this.instanceLines)
    },

    showEndUseSort () {
      return this.rows.some(row => row.line.endUseSort)
    },

    headers () {
      return [
        { text: this.$t('line'), value: 'comparison', sortable: false },
        { text: this.$t('speciesGroup'), value: 'speciesGroup', sortable: false },
        { text: this.$t('lengthGroup'), value: 'lengthGroup', sortable: false },
        { text: this.$t('diameterGroup'), value: 'diameterGroup', sortable: false },
        { text: this.$t('grade'), value: 'grade', sortable: false },
        this.showEndUseSort ? { text: this.$t('endUseSort'), value: 'endUseSort', sortable: false } : undefined,
        { text: this.$t('definition'), value: 'definitionRate', align: 'right', sortable: false },
        { text: this.$t('instance'), value: 'instanceRate', align: 'right', sortable: false },
        { text: this.$t('difference'), value: 'difference', align: 'right', sortable: false }
      ].filter(Boolean)
    }
  },

  methods: {
    rateString (rate) {
      return rate === null ? '—' : formatMoney(rate)
    },

    rowClass (item) {
      return item.comparison === LineComparison.Match ? '' : 'orange lighten-5'
    },

    comparisonIcon (comparison) {
      return comparison === LineComparison.Match ? 'mdi-check-circle' : 'mdi-alert-circle'
    },

    comparisonColor (comparison) {
      return comparison === LineComparison.Match ? 'success' : 'warning'
    },

    comparisonLabelKey (comparison) {
      switch (comparison) {
        case LineComparison.Match: return 'lineMatch'
        case LineComparison.MissingFromInstance: return 'lineMissingFromInstance'
        case LineComparison.RateDiffers: return 'lineRateDiffers'
        case LineComparison.NotInDefinition: return 'lineNotInDefinition'
      }
      return ''
    }
  }
}
</script>
